import { getCachedFingerprint } from "./browserFingerprint";
import { getGuestOwnerToken } from "./guestIdentityService";
import { logger } from "./logger";
import { supabase } from "./supabase";

export interface GuestEmailData {
  guestId: string;
  email: string;
  verified?: boolean;
}

/**
 * Service pour gérer les emails des utilisateurs invités (RGPD)
 */
export const guestEmailService = {
  /**
   * Enregistre l'email d'un invité via RPC SECURITY DEFINER save_guest_email.
   * First-writer wins (ON CONFLICT DO NOTHING côté DB).
   */
  async saveGuestEmail(email: string): Promise<boolean> {
    try {
      const guestId = await getCachedFingerprint();

      const { error } = await supabase.rpc("save_guest_email", {
        p_guest_id: guestId,
        p_email: email,
        // #832 : la ligne est stockée sous le hash du jeton de possession
        // (clé inforgeable), plus sous le fingerprint dérivable.
        p_owner_token: getGuestOwnerToken(),
      });

      if (error) {
        logger.error("Failed to save guest email", "quota", error);
        return false;
      }

      logger.info("Guest email saved successfully", "quota", {
        guestId: guestId.substring(0, 8),
      });
      return true;
    } catch (error) {
      logger.error("Error in saveGuestEmail", "quota", error);
      return false;
    }
  },

  /**
   * Récupère l'email enregistré pour l'invité actuel.
   *
   * Passe par la RPC get_guest_email_by_fingerprint (SECURITY DEFINER).
   * #832 : la lecture est scoppée au jeton de possession (clé inforgeable) —
   * la lecture par fingerprint, dérivable par n'importe quel site visité,
   * permettait d'exfiltrer l'email d'une victime ciblée.
   */
  async getGuestEmail(): Promise<string | null> {
    try {
      const guestId = await getCachedFingerprint();

      const { data, error } = await supabase.rpc(
        "get_guest_email_by_fingerprint",
        { p_guest_id: guestId, p_owner_token: getGuestOwnerToken() },
      );

      if (error) {
        logger.error("Failed to fetch guest email via RPC", "quota", error);
        return null;
      }

      const row = Array.isArray(data) ? data[0] : null;
      return row?.email ?? null;
    } catch (error) {
      logger.error("Error in getGuestEmail", "quota", error);
      return null;
    }
  },
};
